import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Tag {
  id: string;
  brand_id: string;
  name: string;
  color: string;
  created_at: string;
}

// Course Tags - Brand-level library
export function useCourseTagsLibrary(brandId: string | null) {
  const queryClient = useQueryClient();

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['course-tags-library', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      const { data, error } = await supabase
        .from('os_lms_course_tags')
        .select('*')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;
      return data as Tag[];
    },
    enabled: !!brandId,
  });

  const createTag = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      if (!brandId) throw new Error('Brand ID is required');
      
      const { data, error } = await supabase
        .from('os_lms_course_tags')
        .insert({ brand_id: brandId, name, color })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course-tags-library', brandId] });
      toast.success('Tag created');
    },
    onError: (error: any) => {
      toast.error('Failed to create tag: ' + error.message);
    },
  });

  const deleteTag = useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await supabase
        .from('os_lms_course_tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course-tags-library', brandId] });
      toast.success('Tag deleted');
    },
    onError: (error: any) => {
      toast.error('Failed to delete tag: ' + error.message);
    },
  });

  return {
    tags,
    isLoading,
    createTag: createTag.mutate,
    deleteTag: deleteTag.mutate,
  };
}

// Module Tags - Brand-level library
export function useModuleTagsLibrary(brandId: string | null) {
  const queryClient = useQueryClient();

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['module-tags-library', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      const { data, error } = await supabase
        .from('os_lms_module_tags')
        .select('*')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;
      return data as Tag[];
    },
    enabled: !!brandId,
  });

  const createTag = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      if (!brandId) throw new Error('Brand ID is required');
      
      const { data, error } = await supabase
        .from('os_lms_module_tags')
        .insert({ brand_id: brandId, name, color })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['module-tags-library', brandId] });
      toast.success('Tag created');
    },
    onError: (error: any) => {
      toast.error('Failed to create tag: ' + error.message);
    },
  });

  const deleteTag = useMutation({
    mutationFn: async (tagId: string) => {
      const { error } = await supabase
        .from('os_lms_module_tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['module-tags-library', brandId] });
      toast.success('Tag deleted');
    },
    onError: (error: any) => {
      toast.error('Failed to delete tag: ' + error.message);
    },
  });

  return {
    tags,
    isLoading,
    createTag: createTag.mutate,
    deleteTag: deleteTag.mutate,
  };
}

// Lesson Tags - Brand-level library
export function useLessonTagsLibrary(brandId: string | null) {
  const queryClient = useQueryClient();

  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['lesson-tags-library', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      const { data, error } = await supabase
        .from('os_lms_lesson_tags')
        .select('*')
        .eq('brand_id', brandId)
        .order('name');

      if (error) throw error;
      return data as Tag[];
    },
    enabled: !!brandId,
  });

  const createTag = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      if (!brandId) throw new Error('Brand ID is required');
      
      const { data, error } = await supabase
        .from('os_lms_lesson_tags')
        .insert({ brand_id: brandId, name, color })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lesson-tags-library', brandId] });
      toast.success('Tag created');
    },
    onError: (error: any) => {
      toast.error('Failed to create tag: ' + error.message);
    },
  });

  const deleteTag = useMutation({
    mutationFn: async (tagId: string) => {
      const { error} = await supabase
        .from('os_lms_lesson_tags')
        .delete()
        .eq('id', tagId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lesson-tags-library', brandId] });
      toast.success('Tag deleted');
    },
    onError: (error: any) => {
      toast.error('Failed to delete tag: ' + error.message);
    },
  });

  return {
    tags,
    isLoading,
    createTag: createTag.mutate,
    deleteTag: deleteTag.mutate,
  };
}

// Helper to resolve tag details from IDs (for public/student views)
export function useResolveCourseTags(tagIds: string[] | null | undefined) {
  return useQuery({
    queryKey: ['resolved-course-tags', tagIds],
    queryFn: async () => {
      if (!tagIds || tagIds.length === 0) return [];
      
      const { data, error } = await supabase
        .from('os_lms_course_tags')
        .select('*')
        .in('id', tagIds);

      if (error) throw error;
      return data as Tag[];
    },
    enabled: !!tagIds && tagIds.length > 0,
  });
}

export function useResolveModuleTags(tagIds: string[] | null | undefined) {
  return useQuery({
    queryKey: ['resolved-module-tags', tagIds],
    queryFn: async () => {
      if (!tagIds || tagIds.length === 0) return [];
      
      const { data, error } = await supabase
        .from('os_lms_module_tags')
        .select('*')
        .in('id', tagIds);

      if (error) throw error;
      return data as Tag[];
    },
    enabled: !!tagIds && tagIds.length > 0,
  });
}

export function useResolveLessonTags(tagIds: string[] | null | undefined) {
  return useQuery({
    queryKey: ['resolved-lesson-tags', tagIds],
    queryFn: async () => {
      if (!tagIds || tagIds.length === 0) return [];
      
      const { data, error } = await supabase
        .from('os_lms_lesson_tags')
        .select('*')
        .in('id', tagIds);

      if (error) throw error;
      return data as Tag[];
    },
    enabled: !!tagIds && tagIds.length > 0,
  });
}
